import { Typography } from '@mui/material';
import { find } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

import { useSatelliteCreditUsageStats } from '@/api/credits';
import { useSubscriptionsInfo, useTelemetryCreditItems } from '@/api/subscriptions';
import { GetCreditBalanceResponse, useCreditBalance } from '@/api/userAccount';
import { queryClient } from '@/common/react-query';
import { Timestamp } from '@/components/data/Timestamp';
import { PaymentConfirmation } from '@/components/transactions/PaymentConfirmation';
import { RecurringCreditsSetupTip } from '@/components/transactions/RecurringCreditsSetupTip';
import { SatCreditsQuantityEditor } from '@/components/transactions/SatCreditsQuantityEditor';
import { TransactionIntentFooter } from '@/components/transactions/TransactionIntentFooter';
import {
    TotalDueReadout,
    TransactionDescription,
    TransactionModal,
    TransactionSummary,
} from '@/components/transactions/TransactionModal';
import { ConjoinedSummaryCards, SummaryCard, SummaryCardBody } from '@/components/ui/SummaryCard';
import { ONETIME_SATELLITE_CREDITS_PRICE_LOOKUP_KEY, RECURRING_SATELLITE_CREDITS_PRICE_LOOKUP_KEY } from '@/config';
import { TransactionContextProvider, useTransactionContextLineItemQuantity } from '@/context/transaction';

const DEFAULT_ONETIME_CREDITS_QTY = 500;

/**
 * Long enough after a successful transaction for stripe to call our hook and us to grant the purchased credit. We'll force the UI to show
 * the presumed new balance in the meantime, so erring long to make sure to give stripe+tollbooth enough time.
 */
const CREDITS_REFRESH_DELAY = 15_000;

export const SatCreditsOnetimeTransaction = ({
    initialQuantity = DEFAULT_ONETIME_CREDITS_QTY,
}: {
    initialQuantity?: number;
}) => {
    const { data: creditItems } = useTelemetryCreditItems();
    const { data: subscriptionsInfo } = useSubscriptionsInfo();
    const [confirming, setConfirming] = useState(false);
    const item = useMemo(
        () => find(creditItems, { price_lookup_key: ONETIME_SATELLITE_CREDITS_PRICE_LOOKUP_KEY }),
        [creditItems]
    );
    const [currentQuantity, setCurrentQuantity] = useState(initialQuantity);

    const showRecurringCreditsTip = useMemo(() => {
        if (!subscriptionsInfo?.data.length) {
            return false;
        }
        const recurringCreditsItem = find(subscriptionsInfo.data[0]?.items, {
            price_lookup_key: RECURRING_SATELLITE_CREDITS_PRICE_LOOKUP_KEY,
        });
        return !recurringCreditsItem;
    }, [subscriptionsInfo?.data]);

    function handleConfirm() {
        setConfirming(true);
    }

    const handleSuccess = useCallback(() => {
        // there is some delay before Stripe calls us back with the completed transaction and we grant the purchased credits, so
        // after a successful transaction, stick the new balance into the cache directly, and queue up a cache invalidated to fetch
        // the server's version after a little delay
        queryClient.setQueryData(['user-credit-balance'], (creditBalance?: GetCreditBalanceResponse) => {
            if (!currentQuantity) {
                return creditBalance;
            }
            const { data, ...moreCreditBalance } = creditBalance ?? { status: 'success' };
            const { credit_balance, ...moreData } = data ?? { credit_balance: 0, type: 'standard' };
            return { data: { credit_balance: credit_balance + currentQuantity, ...moreData }, ...moreCreditBalance };
        });
        setTimeout(() => queryClient.invalidateQueries({ queryKey: ['user-credit-balance'] }), CREDITS_REFRESH_DELAY);
    }, [currentQuantity]);

    return (
        <TransactionContextProvider type="payment" defaultBackTarget="payment">
            <TransactionModal>
                <TransactionDescription title="Add one-time satellite credits">
                    Choose how many credits you want to purchase. The credits are available immediately and the full
                    amount is due right now.
                </TransactionDescription>
                <TransactionSummary>
                    <SatCreditsQuantityEditor
                        defaultQuantity={initialQuantity}
                        item={item}
                        onChange={setCurrentQuantity}
                    />
                    <TotalDueReadout />
                </TransactionSummary>
                <CreditsEstimatedToLastSummary priceId={item?.price_id} />
                <TransactionIntentFooter onConfirm={handleConfirm} />
            </TransactionModal>
            <PaymentConfirmation
                open={confirming}
                onClose={() => setConfirming(false)}
                onSuccess={handleSuccess}
                successMessage="Your one-time satellite credits have been added."
                paymentProcessingMessage="Your payment is still processing. Your credits will be added once the payment has completed, which may take a few days."
            >
                {showRecurringCreditsTip && <RecurringCreditsSetupTip />}
            </PaymentConfirmation>
        </TransactionContextProvider>
    );
};

export const CreditsEstimatedToLastSummary = ({ priceId }: { priceId?: string }) => {
    const [purchaseQuantity] = useTransactionContextLineItemQuantity(priceId);
    const { data: creditBalance, isSuccess: creditBalanceSuccess } = useCreditBalance();
    const newTotalCredits = purchaseQuantity + (creditBalance?.data?.credit_balance ?? 0);
    const { data: { runOutOfCreditsEstimate } = {} } = useSatelliteCreditUsageStats({
        creditsForRunOutEstimate: newTotalCredits,
    });

    if (!runOutOfCreditsEstimate) {
        return false;
    }

    return (
        <ConjoinedSummaryCards data-testid="credit-run-out-estimate">
            <SummaryCard title={creditBalanceSuccess ? 'New total satellite credits' : 'Satellite credits'}>
                <SummaryCardBody>{newTotalCredits}</SummaryCardBody>
            </SummaryCard>
            <SummaryCard sx={{ justifyContent: 'center' }}>
                <Typography variant="body2">
                    Credits estimated to last until{' '}
                    <strong>
                        <Timestamp value={runOutOfCreditsEstimate} unit="date" />
                    </strong>{' '}
                    based on average usage of currently enabled devices.
                </Typography>
            </SummaryCard>
        </ConjoinedSummaryCards>
    );
};
